import { useEffect, useState } from "react";
import { listBookings, updateBookingStatus, deleteBooking } from "../lib/api";
import { toast } from "sonner";
import { Trash2, RefreshCw, CheckCircle, XCircle, Clock3 } from "lucide-react";

const statusColors = {
  pending: "bg-[#C9A34E]/15 text-[#C9A34E] border-[#C9A34E]/30",
  confirmed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  completed: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  cancelled: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const data = await listBookings();
      setBookings(data);
    } catch {
      toast.error("Could not load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const change = async (id, status) => {
    try {
      await updateBookingStatus(id, status);
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await deleteBooking(id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div data-testid="admin-bookings-page" className="pt-28 pb-24 bg-[#0B0B0B] text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Internal</span>
            <h1 className="font-display text-4xl md:text-5xl mt-4">Bookings Dashboard</h1>
            <p className="text-white/65 mt-3 text-sm">{bookings.length} total bookings</p>
          </div>
          <button onClick={load} data-testid="refresh-btn" className="inline-flex items-center gap-2 border border-[#C9A34E]/40 text-[#C9A34E] rounded-full px-5 py-2.5 text-xs tracking-[0.22em] uppercase hover:bg-[#C9A34E] hover:text-[#0B0B0B] transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="flex gap-3 mt-8 overflow-x-auto">
          {["all", "pending", "confirmed", "completed", "cancelled"].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)} className={`tab-pill ${filter === s ? "active" : ""}`}>{s}</button>
          ))}
        </div>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-[#C9A34E]/15">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-[#C9A34E]">
              <tr className="text-left">
                {["Name", "Contact", "Service", "When", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-4 font-medium tracking-[0.18em] uppercase text-[11px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="p-12 text-center text-white/50">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="p-12 text-center text-white/50">No bookings yet.</td></tr>
              )}
              {!loading && filtered.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[#C9A34E]/10 hover:bg-[#141414]/60 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-white">{b.name}</p>
                    <p className="text-white/45 text-xs mt-1">{b.message?.slice(0, 60) || "—"}</p>
                  </td>
                  <td className="px-5 py-4 text-white/75">
                    <p>{b.phone}</p>
                    <p className="text-white/45 text-xs">{b.email}</p>
                  </td>
                  <td className="px-5 py-4 text-[#C9A34E]">{b.service}</td>
                  <td className="px-5 py-4 text-white/75">{b.preferred_date}<br /><span className="text-xs text-white/45">{b.preferred_time}</span></td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.2em] ${statusColors[b.status] || statusColors.pending}`}>{b.status}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-2">
                      <button data-testid={`confirm-${b.id}`} onClick={() => change(b.id, "confirmed")} className="p-2 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/15" title="Confirm"><CheckCircle size={14} /></button>
                      <button data-testid={`complete-${b.id}`} onClick={() => change(b.id, "completed")} className="p-2 rounded-full border border-sky-500/30 text-sky-300 hover:bg-sky-500/15" title="Complete"><Clock3 size={14} /></button>
                      <button data-testid={`cancel-${b.id}`} onClick={() => change(b.id, "cancelled")} className="p-2 rounded-full border border-rose-500/30 text-rose-300 hover:bg-rose-500/15" title="Cancel"><XCircle size={14} /></button>
                      <button data-testid={`delete-${b.id}`} onClick={() => remove(b.id)} className="p-2 rounded-full border border-white/15 text-white/60 hover:bg-white/10" title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
